import { Category } from '@shared/types/product/Category';

// EWM-595 — the names allowed in the Category facet on a category PLP: the
// current category's *immediate* sub-categories only.
//
// The Algolia `categories.name` facet carries each product's *entire* category
// hierarchy, so on a category PLP it lists a flat soup of every level — the
// current ("main") category, its ancestors, AND deeper descendants. The first
// pass (just hiding the main category + ancestors) still left grandchildren
// in: on the "Components & Switches" PLP the facet showed "Resistors" next to
// its grandchild "Wire-wound" and "Cables & Connectors" next to "DC Power
// Cable" / "General cable" — all matching the same product, which read as
// duplicate/overlapping options (the WH3100 example QA flagged).
//
// Showing only the current category's *direct children* removes the ancestors,
// the main category, and the deeper descendants in one rule, leaving a clean
// single level of "only relevant sub-categories".
//
// A flat category is a direct child of `category` when the closest entry in its
// root-first `ancestors` chain (the last element) is `category` itself. Matched
// by `categoryId` (stable) rather than name.
//
// `/search` has no current category, so callers skip this filtering and show
// the full cross-result-set category list.
// EWM-832 — the same rule, returning the child NODES rather than just their
// names, because the subcategory navigation block needs each child's `_url`.
//
// Incoming array order is preserved: `queryCategories` sorts by the
// commercetools `orderHint`, so siblings arrive in the order authored in CT and
// keeping that order is what "the explicit order held against them in
// commercetools" means in practice. See the note on `buildSubcategoryNav`.
export const collectImmediateChildCategories = <T extends Pick<Category, 'ancestors'>>(
  category: Pick<Category, 'categoryId'> | undefined,
  allCategories: T[],
): T[] => {
  const currentId = category?.categoryId;
  if (!currentId) return [];
  return allCategories.filter(
    (candidate) => candidate.ancestors?.[candidate.ancestors.length - 1]?.categoryId === currentId,
  );
};

export const collectImmediateChildCategoryNames = (
  category: Pick<Category, 'categoryId'> | undefined,
  allCategories: Pick<Category, 'name' | 'ancestors'>[],
): Set<string> => {
  const names = new Set<string>();
  for (const candidate of collectImmediateChildCategories(category, allCategories)) {
    if (candidate.name) names.add(candidate.name);
  }
  return names;
};

// EWM-185 / EWM-240 / EWM-599 — turn a `categoryFilter.filterattribute` value
// into the drawer accordion heading.
//
// The authored convention is PascalCase words joined by underscores, e.g.
// `Meter_Safety_Rating` → "Meter Safety Rating". Two refinements over the
// original underscore-split-and-title-case (EWM-599):
//
//   1. If the value already reads as a human label (contains whitespace),
//      pass it through untouched — re-title-casing "3D Printer and Filament"
//      would wrongly capitalise "and" → "And". This lets authors write the
//      exact label when the underscore convention can't express it.
//   2. Upper-case a digit-led token whole (`3d` → "3D", `12v` → "12V"); the
//      original only upper-cased the first char, which is the digit, leaving
//      "3d". Legacy values have no such tokens, so this is purely additive.
export const formatFilterLabel = (raw: string): string => {
  const trimmed = raw.trim();
  if (/\s/.test(trimmed)) return trimmed;
  return trimmed
    .split('_')
    .filter(Boolean)
    .map((word) => (/^\d/.test(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};
